import type { PropertyPublic, WorldManifest } from "@townsy/contracts";
import { getInterior, getPlot, homeSeats, nearestFreeSeat, storeSeats, townSeats, type Seat } from "@townsy/world";
import { environmentInputs, interiorPlacements, yardPlacements, type PredictionRoom } from "./colliders";

/**
 * The seats of the current room (ADR-0018), listed by the SAME shared functions and from the same public property data
 * the realtime server uses (packages/world seats.ts): the street's benches and the owned homes' yard seats, a home's
 * furniture, a store's theme. The browser only offers "Sit" / "Lie down" and draws postures from this list; the server
 * decides who sits where. Town v6 (ADR-0042): the street's seats are the whole town's (world `townSeats`: Central's
 * benches `bench:<i>:<n>`, Riverside's and Hillcrest's `bench:<i>:<districtId>:<n>`, and the yards of every district's
 * homes), as the one street room lists them.
 */
export function roomSeats(manifest: WorldManifest, room: PredictionRoom, properties: readonly PropertyPublic[]): { seats: Seat[]; key: string } {
  const key = environmentInputs(room, properties).key;
  if (room.kind === "interior" && room.propertyId) {
    const plot = getPlot(manifest, room.propertyId);
    const property = properties.find((p) => p.id === room.propertyId) ?? null;
    if (plot?.type === "RESIDENTIAL") return { seats: homeSeats(getInterior(manifest, plot.interiorTemplate), interiorPlacements(property)), key };
    if (plot?.type === "COMMERCIAL") return { seats: storeSeats(property?.interiorTheme ?? null), key };
    return { seats: [], key };
  }
  return { seats: townSeats(manifest, yardPlacements(properties)), key };
}

/** What the bottom prompt offers: the nearest free seat within reach. */
export interface SeatOffer {
  seatId: string;
  kind: "sit" | "lie";
  /** "on the sofa", "in the hot tub"... */
  place: string;
}

/** The seat to offer from `pos` (free = nobody else's ActorState.seatId), or null. */
export function seatOffer(seats: readonly Seat[], pos: { x: number; z: number }, taken: ReadonlySet<string>): SeatOffer | null {
  const seat = nearestFreeSeat(seats, pos, taken);
  return seat ? { seatId: seat.id, kind: seat.kind, place: seat.place } : null;
}

/** "Sit on the sofa" / "Lie down on the bed". */
export function seatActionLabel(offer: Pick<SeatOffer, "kind" | "place">): string {
  return `${offer.kind === "lie" ? "Lie down" : "Sit"} ${offer.place}`;
}

/** "You are sitting on the sofa" / "You are lying on the bed". */
export function postureText(kind: "sit" | "lie", place: string | null): string {
  const verb = kind === "lie" ? "lying" : "sitting";
  return place ? `You are ${verb} ${place}` : `You are ${verb} down`;
}
